import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner, Badge } from '../../../shared/components/ui';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { vocabularyApi } from '../../../shared/services/settingsApi';

function TabButton({ active, onClick, children, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
        active
          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300'
      }`}
    >
      {children}
      {badge ? (
        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function CsiDivisionInput({ value, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => { setDraft(value || ''); }, [value]);

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        className="px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50"
        title="Click to change CSI division"
      >
        Div {value || '—'}
      </button>
    );
  }
  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed && /^\d{1,2}$/.test(trimmed) && trimmed !== value) {
      await onSave(trimmed.padStart(2, '0'));
    }
    setEditing(false);
  };
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
      }}
      className="w-12 px-1 py-0.5 text-xs border border-blue-400 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
      placeholder="00"
    />
  );
}

function CanonicalNameCell({ value, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => { setDraft(value || ''); }, [value]);

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        className="text-left text-sm text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
        title="Click to rename"
      >
        {value || <span className="italic text-gray-400">(empty)</span>}
      </button>
    );
  }
  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      await onSave(trimmed);
    }
    setEditing(false);
  };
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
      }}
      className="w-full px-2 py-1 text-sm border border-blue-400 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    />
  );
}

function EntriesTab({ entries, onUpdate, onMerge, onDelete, isBusy }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [survivingId, setSurvivingId] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const needle = search.toLowerCase();
    return entries.filter((e) => (e.canonicalName || '').toLowerCase().includes(needle));
  }, [entries, search]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      // If the survivor was deselected, clear the survivor flag.
      if (!next.has(survivingId)) setSurvivingId(null);
      return next;
    });
  };

  const canMerge = selected.size >= 2 && survivingId && selected.has(survivingId);

  const onMergeClick = async () => {
    const mergedIds = [...selected].filter((id) => id !== survivingId);
    await onMerge(survivingId, mergedIds);
    setSelected(new Set());
    setSurvivingId(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Search names..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
        {selected.size >= 2 ? (
          <Button
            size="sm"
            onClick={onMergeClick}
            disabled={!canMerge || isBusy}
            title={canMerge ? 'Merge into the chosen survivor' : 'Pick a surviving entry from the selection'}
          >
            Merge {selected.size} into 1
          </Button>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-12">
          {entries.length === 0
            ? 'No vocabulary entries yet. Once you import or create estimates, line items will appear here.'
            : 'No entries match your search.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 w-10"></th>
                <th className="px-3 py-2">Canonical Name</th>
                <th className="px-3 py-2 w-24">CSI</th>
                <th className="px-3 py-2 w-20 text-right">Used</th>
                <th className="px-3 py-2 w-24">Source</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => {
                const isSelected = selected.has(entry.vocabEntryId);
                const isSurvivor = survivingId === entry.vocabEntryId;
                return (
                  <tr
                    key={entry.vocabEntryId}
                    className={`border-t border-gray-100 dark:border-gray-800 ${
                      isSurvivor ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(entry.vocabEntryId)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <CanonicalNameCell
                        value={entry.canonicalName}
                        onSave={(name) => onUpdate(entry.vocabEntryId, { canonicalName: name })}
                        disabled={isBusy}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <CsiDivisionInput
                        value={entry.csiDivisionCode}
                        onSave={(code) => onUpdate(entry.vocabEntryId, { csiDivisionCode: code })}
                        disabled={isBusy}
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{entry.usageCount ?? 0}</td>
                    <td className="px-3 py-2">
                      <Badge variant="gray" size="sm">{entry.source || '—'}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isSelected ? (
                        <button
                          type="button"
                          onClick={() => setSurvivingId(isSurvivor ? null : entry.vocabEntryId)}
                          className={`text-xs px-2 py-0.5 rounded ${
                            isSurvivor
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/40'
                          }`}
                        >
                          {isSurvivor ? 'Survivor' : 'Pick survivor'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onDelete(entry)}
                          disabled={(entry.usageCount ?? 0) > 0 || isBusy}
                          className="text-xs px-2 py-0.5 rounded text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={(entry.usageCount ?? 0) > 0 ? 'Cannot delete an entry that is still in use; merge it instead' : 'Delete entry'}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SuggestionsTab({ suggestions, onMerge, onDismiss, isBusy }) {
  if (suggestions.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-12">
        No suggestions right now. As you import more estimates, near-duplicate names will surface here for review.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {suggestions.map((s) => (
        <Card key={s.suggestionId}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="text-sm">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{s.entryA.canonicalName}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Div {s.entryA.csiDivisionCode || '—'} · used {s.entryA.usageCount ?? 0}
                  </div>
                </div>
                <div className="text-sm">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{s.entryB.canonicalName}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Div {s.entryB.csiDivisionCode || '—'} · used {s.entryB.usageCount ?? 0}
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Match: {Math.round(s.similarityScore || 0)}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onMerge(s.entryA.vocabEntryId, [s.entryB.vocabEntryId])}
                  disabled={isBusy}
                  title={`Keep "${s.entryA.canonicalName}" and merge "${s.entryB.canonicalName}" into it`}
                >
                  Keep A
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onMerge(s.entryB.vocabEntryId, [s.entryA.vocabEntryId])}
                  disabled={isBusy}
                  title={`Keep "${s.entryB.canonicalName}" and merge "${s.entryA.canonicalName}" into it`}
                >
                  Keep B
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDismiss(s.suggestionId)}
                  disabled={isBusy}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function Vocabulary() {
  const [tab, setTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [entriesData, suggestionsData] = await Promise.all([
        vocabularyApi.list(),
        vocabularyApi.listSuggestions(),
      ]);
      setEntries(entriesData);
      setSuggestions(suggestionsData);
    } catch (err) {
      console.error('Failed to load vocabulary:', err);
      setError(err.message || 'Failed to load vocabulary');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUpdate = async (vocabEntryId, updates) => {
    setIsBusy(true);
    try {
      await vocabularyApi.update(vocabEntryId, updates);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Failed to update entry');
    } finally {
      setIsBusy(false);
    }
  };

  const handleMerge = async (survivingId, mergedIds) => {
    setIsBusy(true);
    try {
      await vocabularyApi.merge(survivingId, mergedIds);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Failed to merge entries');
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setIsBusy(true);
    try {
      await vocabularyApi.delete(pendingDelete.vocabEntryId);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Failed to delete entry');
    } finally {
      setIsBusy(false);
      setPendingDelete(null);
    }
  };

  const handleDismiss = async (suggestionId) => {
    setIsBusy(true);
    try {
      await vocabularyApi.dismissSuggestion(suggestionId);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Failed to dismiss suggestion');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Line-item Vocabulary</CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Your tenant's canonical line-item names. New imports fuzzy-match against this list so duplicates don't pile up.
            Edit, merge, or recategorize entries below.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex border-b border-gray-200 dark:border-gray-800 mb-4">
            <TabButton active={tab === 'entries'} onClick={() => setTab('entries')}>
              Entries ({entries.length})
            </TabButton>
            <TabButton
              active={tab === 'suggestions'}
              onClick={() => setTab('suggestions')}
              badge={suggestions.length || null}
            >
              Merge suggestions
            </TabButton>
          </div>

          {error ? (
            <div className="mb-3 p-3 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          ) : null}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : tab === 'entries' ? (
            <EntriesTab
              entries={entries}
              onUpdate={handleUpdate}
              onMerge={handleMerge}
              onDelete={(entry) => setPendingDelete(entry)}
              isBusy={isBusy}
            />
          ) : (
            <SuggestionsTab
              suggestions={suggestions}
              onMerge={handleMerge}
              onDismiss={handleDismiss}
              isBusy={isBusy}
            />
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete vocabulary entry?"
        message={pendingDelete ? `Delete "${pendingDelete.canonicalName}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

export default Vocabulary;
